import { Helmet } from 'react-helmet-async';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { Target, Users, Zap, Shield, MapPin, Ruler, Layers, FileCheck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export default function About() {
  const { openAuthModal } = useAuth();

  return (
    <div className="min-h-screen bg-slate-950">
      <Helmet>
        <title>About SquareCount | Satellite Roof Measurement Software for Contractors</title>
        <meta name="description" content="SquareCount turns satellite imagery into estimate-grade roof measurements and editable material lists for shingle, tile, and metal — in about 60 seconds, at up to 90% less than EagleView. Learn how it works and who it's built for." />
        <link rel="canonical" href="https://squarecount.io/about" />
        <meta property="og:title" content="About SquareCount" />
        <meta property="og:description" content="How SquareCount measures roofs from satellite imagery and builds material lists for shingle, tile & metal — estimate-grade, up to 90% less than EagleView." />
        <meta property="og:url" content="https://squarecount.io/about" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "url": "https://squarecount.io/about",
          "mainEntity": {
            "@type": "Organization",
            "name": "SquareCount",
            "url": "https://squarecount.io",
            "description": "Satellite-based roof measurement software for roofing contractors. Estimate-grade measurements and editable material lists for shingle, tile, and metal.",
            "areaServed": { "@type": "Country", "name": "United States" },
            "knowsAbout": ["roof measurement", "roofing material estimation", "satellite roof measurement", "roofing takeoffs", "shingle, tile and metal roofing"]
          }
        })}</script>
      </Helmet>
      <Navbar />

      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-orange-900 text-white py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl mb-6">
            Roof Measurement Software, Built for the People Doing the Estimating
          </h1>
          <p className="text-xl text-slate-300">
            We turn satellite imagery into estimate-grade roof measurements and material lists for shingle, tile, and metal —
            so contractors can quote jobs in minutes instead of days, without paying per report.
          </p>
        </div>
      </section>

      {/* Our Story */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <div className="prose prose-lg prose-invert max-w-none">
          <h2 className="font-heading font-bold text-3xl text-white mb-6">Why we built SquareCount</h2>
          <p className="text-slate-300 text-lg mb-6">
            Roofing contractors face a bad choice on every job: pay $40–75 for a single measurement report and wait a day or two
            for it, or climb the roof with a tape and risk the fall. Meanwhile, high-resolution satellite imagery — the same data
            that powers the expensive reports — was already good enough to measure most roofs from a desk.
          </p>
          <p className="text-slate-300 text-lg mb-6">
            The frustrations we kept hearing from contractors were always the same: the established tools were too expensive for a
            small shop, results took too long to quote on the spot, team seats cost extra, and you couldn't put your own brand on
            the report you handed the homeowner.
          </p>
          <p className="text-slate-300 text-lg mb-6">
            So we built the tool we'd want to use: type an address, trace the roof, and get pitch-adjusted measurements and a
            material list in about a minute — for a flat monthly price instead of per report. Then we kept going, because a
            measurement is only half the job.
          </p>
        </div>
      </section>

      {/* How it works — demonstrates expertise (E-E-A-T) */}
      <section className="bg-slate-900/40 border-y border-slate-800 py-16">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="font-heading font-bold text-3xl text-white mb-3">How SquareCount measures a roof</h2>
          <p className="text-slate-400 mb-10 max-w-3xl">
            Our numbers are <strong className="text-slate-200">estimate-grade</strong> — built for fast, repeatable roofing
            estimates. Here's exactly how we get them, and where you should still verify on-site.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { icon: MapPin, t: 'High-resolution satellite imagery', d: 'You enter any U.S. address and we load current high-resolution overhead imagery for that property — no ladder, no on-site visit, no 24–48 hour wait.' },
              { icon: Ruler, t: 'Pitch-adjusted area & perimeter', d: 'You trace the roof outline (or work per facet). We compute the flat footprint, then apply the pitch factor to get true surface area, squares, and perimeter — plus valley and ridge linear footage when you draw them.' },
              { icon: Layers, t: 'Material-specific takeoffs', d: 'Pick shingle, tile, or metal and the list rebuilds in the right units — bundles and squares for shingle, pieces for tile, panels and trim for metal — with a waste factor you control.' },
              { icon: FileCheck, t: 'Verify, then quote', d: 'Every report is labeled estimate-grade. We tell you to confirm final quantities on-site before ordering material — because the person on the roof is still the expert, not the software.' },
            ].map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="bg-slate-900/60 border border-slate-800 rounded-xl p-6">
                  <div className="w-11 h-11 bg-orange-500/20 rounded-lg flex items-center justify-center mb-3">
                    <Icon className="w-6 h-6 text-orange-400" />
                  </div>
                  <h3 className="font-heading font-bold text-lg text-white mb-1.5">{s.t}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{s.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* What it does now — reflects all the recent updates */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <h2 className="font-heading font-bold text-3xl text-white mb-6">What SquareCount does today</h2>
        <p className="text-slate-300 text-lg mb-6">
          It started as a shingle measurement tool. It's now a full estimating workflow for roofing contractors:
        </p>
        <ul className="text-slate-300 text-lg space-y-2 mb-6 list-disc pl-6">
          <li><strong className="text-white">Measure any roof</strong> from satellite — pitch-adjusted area, squares, perimeter, and multi-section support.</li>
          <li><strong className="text-white">Shingle, tile &amp; metal</strong> material lists that adjust to the roofing material you're quoting.</li>
          <li><strong className="text-white">Valley &amp; ridge takeoffs</strong> drawn on the roof, with linear footage on every report, PDF, and CSV.</li>
          <li><strong className="text-white">Editable reports &amp; custom materials</strong> — change any quantity, add your own line items.</li>
          <li><strong className="text-white">A job cost estimator</strong> to price materials plus labor, dumpster, and permits — kept private to you.</li>
          <li><strong className="text-white">Shareable, white-label client reports</strong> with your logo and branding.</li>
        </ul>
        <p className="text-slate-400">
          See the full breakdown on the <Link to="/features" className="text-orange-400 hover:text-orange-300 underline">Features page</Link>,
          or compare plans on <Link to="/pricing" className="text-orange-400 hover:text-orange-300 underline">Pricing</Link>.
        </p>
      </section>

      {/* Values */}
      <section className="bg-slate-900/40 border-y border-slate-800 py-16">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="font-heading font-bold text-3xl text-white mb-8">What we stand by</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="p-6 bg-slate-900/60 border border-slate-800 rounded-lg">
              <Target className="w-12 h-12 text-orange-500 mb-4" />
              <h3 className="font-heading font-bold text-xl text-white mb-3">Honest, flat pricing</h3>
              <p className="text-slate-400">No per-report fees and no per-seat surprises. One flat monthly plan, up to 90% less than per-report services like EagleView. Cancel anytime.</p>
            </div>
            <div className="p-6 bg-slate-900/60 border border-slate-800 rounded-lg">
              <Zap className="w-12 h-12 text-orange-500 mb-4" />
              <h3 className="font-heading font-bold text-xl text-white mb-3">Speed on the job</h3>
              <p className="text-slate-400">Measurements in about 60 seconds, not 24–48 hours, so you can quote on-site and win more bids.</p>
            </div>
            <div className="p-6 bg-slate-900/60 border border-slate-800 rounded-lg">
              <Shield className="w-12 h-12 text-orange-500 mb-4" />
              <h3 className="font-heading font-bold text-xl text-white mb-3">Straight talk on accuracy</h3>
              <p className="text-slate-400">We call our measurements estimate-grade and tell you to verify on-site. No inflated accuracy claims — just fast, repeatable numbers you can build an estimate on.</p>
            </div>
            <div className="p-6 bg-slate-900/60 border border-slate-800 rounded-lg">
              <Users className="w-12 h-12 text-orange-500 mb-4" />
              <h3 className="font-heading font-bold text-xl text-white mb-3">Built from contractor feedback</h3>
              <p className="text-slate-400">Tile and metal lists, valley/ridge takeoffs, editable reports, and job costing all came from what contractors told us they were missing.</p>
            </div>
          </div>
        </div>
      </section>

      {/* By the numbers — only defensible/capability claims */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <h2 className="font-heading font-bold text-3xl text-white mb-8 text-center">The short version</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="text-4xl font-bold text-orange-500 mb-2">~60s</div>
            <div className="text-slate-400">Per measurement</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-orange-500 mb-2">Up to 90%</div>
            <div className="text-slate-400">Less than EagleView</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-orange-500 mb-2">All 50</div>
            <div className="text-slate-400">U.S. states</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-orange-500 mb-2">7 days</div>
            <div className="text-slate-400">Free Contractor trial</div>
          </div>
        </div>
        {/* PLACEHOLDER: when you have real, verifiable numbers (contractors using it, roofs measured,
            reports generated, launch year) we should add them here and back them with proof — that is a
            strong E-E-A-T signal. Until then we only state capability/price facts we can defend. */}
      </section>

      {/* Who's behind it — E-E-A-T authorship placeholder */}
      <section className="bg-slate-900/40 border-y border-slate-800 py-16">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-heading font-bold text-3xl text-white mb-4">Who's behind SquareCount</h2>
          <p className="text-slate-400">
            {/* PLACEHOLDER (high-value E-E-A-T): replace with a real founder/team name, a 2–3 sentence bio,
                and their roofing or software background. A named, credible person behind the product is one
                of the strongest "Expertise/Authoritativeness" signals Google looks for. Add a photo + link
                to a LinkedIn if possible. */}
            SquareCount is built and supported by a small team focused on one thing: making professional roof
            measurement fast and affordable for working contractors. Questions or feedback go straight to a real
            person — reach us anytime on the <Link to="/contact" className="text-orange-400 hover:text-orange-300 underline">contact page</Link>.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-6 py-16 text-center">
        <h2 className="font-heading font-bold text-3xl text-white mb-4">Try the full toolkit free for 7 days</h2>
        <p className="text-slate-400 mb-8 max-w-2xl mx-auto">
          Full Contractor access — shingle, tile &amp; metal, valley/ridge takeoffs, editable reports, job costing,
          and shareable client reports. No credit card.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            size="lg"
            className="bg-orange-500 hover:bg-orange-400 text-white font-heading font-bold text-lg px-8"
            onClick={() => openAuthModal('register')}
          >
            Start Your 7-Day Contractor Trial
          </Button>
          <Link to="/pricing">
            <Button size="lg" variant="outline" className="font-heading font-bold border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white">
              View Pricing
            </Button>
          </Link>
        </div>
      </section>
      <Footer />
    </div>
  );
}
